import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Edit,
  Mail,
  Phone,
  User as UserIcon,
  CheckCircle,
  XCircle,
  Calendar,
  Shield,
  ShoppingBag,
  Lock,
  Unlock,
  Trash2,
  AlertTriangle,
  LogIn,
  Smartphone,
  Monitor,
  RefreshCw,
  UserX,
  UserCheck
} from 'lucide-react'
import useUserStore from '@/stores/userStore'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { UserSession } from '@/services/userService'

const UserDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    currentUser,
    fetchUserById,
    loading,
    error,
    deactivateUser,
    reactivateUser,
    anonymizeUser,
    lockUser,
    unlockUser,
    userSessions,
    userOAuthAccounts,
    userLoginHistory,
    sessionsLoading,
    oauthLoading,
    loginHistoryLoading,
    fetchUserSessions,
    fetchUserOAuthAccounts,
    fetchUserLoginHistory,
    terminateSession,
    terminateAllSessions,
    clearExtendedData
  } = useUserStore()

  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [showImpersonateModal, setShowImpersonateModal] = useState(false)
  const [impersonateReason, setImpersonateReason] = useState('')
  const [currentAdmin, setCurrentAdmin] = useState<any>(null)
  const [loginHistoryPage, setLoginHistoryPage] = useState(0)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showAnonymizeConfirm, setShowAnonymizeConfirm] = useState(false)

  useEffect(() => {
    if (id) {
      fetchUserById(id)
      fetchUserSessions(id)
      fetchUserOAuthAccounts(id)
      fetchUserLoginHistory(id, 10, 0)
    }
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try { setCurrentAdmin(JSON.parse(userStr)) } catch {}
    }
    return () => { clearExtendedData() }
  }, [id])

  const handleAction = async (action: string, actionFn: () => Promise<any>) => {
    try {
      setActionLoading(action)
      await actionFn()
      if (id) {
        await fetchUserById(id)
        if (action.includes('session')) {
          await fetchUserSessions(id)
        }
      }
    } catch (error) {
      console.error(`Error ${action}:`, error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleImpersonate = async () => {
    if (!id || !impersonateReason.trim()) return
    try {
      const { userService } = await import('@/services/userService')
      const result = await userService.impersonateUser(
        currentAdmin?.userId || 'admin',
        id,
        impersonateReason
      )
      localStorage.setItem('impersonation_token', result.token)
      window.open('/', '_blank')
      setShowImpersonateModal(false)
      setImpersonateReason('')
    } catch (error: any) {
      alert(`Échec de l'usurpation d'identité : ${error.message}`)
    }
  }

  const loadMoreLoginHistory = () => {
    if (id) {
      const newPage = loginHistoryPage + 1
      setLoginHistoryPage(newPage)
      fetchUserLoginHistory(id, 10, newPage * 10)
    }
  }

  if (loading && !currentUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de l'utilisateur...</p>
        </div>
      </div>
    )
  }

  if (error || !currentUser) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Utilisateur introuvable"
          breadcrumbs={[
            { label: 'Utilisateurs', href: '/users' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/users"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <UserIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Utilisateur introuvable</h3>
              <p className="text-gray-500 mb-4">
                {error || "L'utilisateur recherché n'existe pas."}
              </p>
              <Button onClick={() => navigate('/users')}>
                Retour à la liste
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const user = currentUser

  const breadcrumbs = [
    { label: 'Utilisateurs', href: '/users' },
    { label: `${user.first_name} ${user.last_name}`, href: '#' }
  ]

  const actions = [
    {
      label: 'Modifier',
      icon: <Edit className="h-5 w-5" />,
      onClick: () => navigate(`/users/${id}/edit`),
      variant: 'default' as const
    }
  ]

  const getStatusBadge = () => {
    if (user.deleted_at) return { label: 'Supprimé', variant: 'destructive' as const }
    if (user.is_locked) return { label: 'Verrouillé', variant: 'destructive' as const }
    if (user.is_active) return { label: 'Actif', variant: 'success' as const }
    return { label: 'Inactif', variant: 'destructive' as const }
  }

  const status = getStatusBadge()

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'N/D'
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    })
  }

  const getAuthProviderIcon = (provider: string) => {
    const icons: Record<string, string> = {
      google: 'G', facebook: 'f', apple: '🍎', twitter: '𝕏', instagram: '📷'
    }
    return icons[provider] || provider.charAt(0).toUpperCase()
  }

  const getDeviceIcon = (session: UserSession) => {
    const ua = (session.user_agent || '').toLowerCase()
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) return <Smartphone className="h-4 w-4" />
    if (ua.includes('tablet') || ua.includes('ipad')) return <Monitor className="h-4 w-4" />
    return <Monitor className="h-4 w-4" />
  }

  return (
    <div className="space-y-6">
      <DetailHeader
        title={`${user.first_name} ${user.last_name}`}
        subtitle={`ID: ${user.user_id}`}
        breadcrumbs={breadcrumbs}
        backUrl="/users"
        actions={actions}
        status={status}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Account Info Section */}
          <DetailSection title="Informations du compte" variant="card">
            <div className="flex items-start space-x-6">
              <div className="h-20 w-20 flex-shrink-0 bg-primary-100 rounded-lg flex items-center justify-center">
                <UserIcon className="h-10 w-10 text-primary-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    {user.first_name} {user.last_name}
                  </h2>
                  <Badge variant={status.variant === 'success' ? 'default' : 'destructive'}>
                    {status.label}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Courriel</label>
                    <div className="flex items-center text-gray-900 mt-1">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      {user.email}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Téléphone</label>
                    <div className="flex items-center text-gray-900 mt-1">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      {user.phone || 'N/D'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Rôle principal</label>
                    <div className="flex items-center text-gray-900 mt-1">
                      <Shield className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="capitalize">{user.primary_role}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Méthode d'authentification</label>
                    <div className="flex items-center text-gray-900 mt-1">
                      <LogIn className="h-4 w-4 mr-2 text-gray-400" />
                      {user.auth_method || 'password'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Créé le</label>
                    <div className="flex items-center text-gray-900 mt-1">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      {formatDate(user.created_at)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Mis à jour le</label>
                    <div className="flex items-center text-gray-900 mt-1">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      {formatDate(user.updated_at)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DetailSection>

          {/* Account Lifecycle Section */}
          <DetailSection title="Cycle de vie du compte" variant="card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Désactivé le</label>
                <p className="text-gray-900 mt-1">{formatDate(user.deactivated_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Supprimé le</label>
                <p className="text-gray-900 mt-1">{formatDate(user.deleted_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Anonymisé le</label>
                <p className="text-gray-900 mt-1">{formatDate(user.anonymized_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Suppression planifiée</label>
                <p className="text-gray-900 mt-1">{formatDate(user.scheduled_deletion_at)}</p>
              </div>
            </div>
          </DetailSection>

          {/* Security Section */}
          <DetailSection title="Sécurité" variant="card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Compte verrouillé</label>
                <div className="flex items-center mt-1">
                  {user.is_locked ? (
                    <Badge variant="destructive"><Lock className="h-3 w-3 mr-1" /> Verrouillé</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-green-50 text-green-700"><Unlock className="h-3 w-3 mr-1" /> Déverrouillé</Badge>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Verrouillé jusqu'au</label>
                <p className="text-gray-900 mt-1">{formatDate(user.locked_until)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Dernière connexion</label>
                <p className="text-gray-900 mt-1">{formatDate(user.last_login_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">IP de la dernière connexion</label>
                <p className="text-gray-900 mt-1">{user.last_login_ip || 'N/D'}</p>
              </div>
            </div>
          </DetailSection>

          {/* Consent Section */}
          <DetailSection title="Consentement" variant="card">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Conditions acceptées</label>
                <p className="text-gray-900 mt-1">{formatDate(user.terms_accepted_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Politique de confidentialité acceptée</label>
                <p className="text-gray-900 mt-1">{formatDate(user.privacy_policy_accepted_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Consentement marketing</label>
                <p className="text-gray-900 mt-1">
                  {user.marketing_consent ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700">Oui</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-gray-50 text-gray-700">Non</Badge>
                  )}
                </p>
              </div>
            </div>
          </DetailSection>

          {/* Connected OAuth Accounts Section */}
          <DetailSection
            title="Comptes OAuth connectés"
            variant="card"
            loading={oauthLoading}
            empty={userOAuthAccounts.length === 0}
            emptyMessage="Aucun compte OAuth connecté"
          >
            <div className="space-y-3">
              {userOAuthAccounts.map((account) => (
                <div key={account.oauth_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-sm">
                      {getAuthProviderIcon(account.provider)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{account.provider}</p>
                      <p className="text-sm text-gray-500">{account.email}</p>
                    </div>
                  </div>
                  {account.is_primary && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">Principal</Badge>
                  )}
                </div>
              ))}
            </div>
          </DetailSection>

          {/* Active Sessions Section */}
          <DetailSection
            title="Sessions actives"
            variant="card"
            loading={sessionsLoading}
            empty={userSessions.length === 0}
            emptyMessage="Aucune session active"
            actions={userSessions.length > 0 ? [{
              label: 'Tout résilier',
              icon: <XCircle className="h-4 w-4" />,
              onClick: () => handleAction('terminate-all', () => terminateAllSessions(user.user_id)),
              variant: 'destructive' as const
            }] : []}
          >
            <div className="space-y-3">
              {userSessions.map((session) => (
                <div key={session.session_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getDeviceIcon(session)}
                    <div>
                      <p className="font-medium text-gray-900">
                        {session.browser || 'Navigateur inconnu'} sur {session.os || 'OS inconnu'}
                        {session.is_current && (
                          <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 text-xs">Actuelle</Badge>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        IP : {session.ip_address} · Dernière activité : {formatDate(session.last_activity_at)}
                      </p>
                    </div>
                  </div>
                  {!session.is_current && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAction('terminate-session', () => terminateSession(user.user_id, session.session_id))}
                    >
                      <XCircle className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </DetailSection>

          {/* Login History Section */}
          <DetailSection
            title="Historique des connexions"
            variant="card"
            loading={loginHistoryLoading}
            empty={userLoginHistory.length === 0}
            emptyMessage="Aucun historique de connexion disponible"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Date/Heure</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Méthode</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Adresse IP</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {userLoginHistory.map((entry) => (
                    <tr key={entry.login_id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3">{formatDate(entry.created_at)}</td>
                      <td className="py-2 px-3 capitalize">{entry.method}</td>
                      <td className="py-2 px-3">{entry.ip_address}</td>
                      <td className="py-2 px-3">
                        {entry.status === 'success' ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700">Succès</Badge>
                        ) : (
                          <Badge variant="destructive">Échec{entry.failure_reason ? ` : ${entry.failure_reason}` : ''}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex justify-center">
              <Button variant="ghost" size="sm" onClick={loadMoreLoginHistory}>
                <RefreshCw className="h-4 w-4 mr-2" /> Charger plus
              </Button>
            </div>
          </DetailSection>
        </div>

        {/* Right column - Actions & Status */}
        <div className="space-y-6">
          <DetailSection title="Statut" variant="card">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Actif</span>
                {user.is_active ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Vérifié</span>
                {user.is_verified ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Verrouillé</span>
                {user.is_locked ? (
                  <Lock className="h-5 w-5 text-red-600" />
                ) : (
                  <Unlock className="h-5 w-5 text-green-600" />
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Consentement marketing</span>
                {user.marketing_consent ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Rôles" variant="card">
            <div className="flex flex-wrap gap-2">
              {(user.roles || []).length > 0 ? (
                user.roles.map((role) => (
                  <Badge
                    key={role}
                    variant={role === 'admin' ? 'destructive' : role === 'merchant' ? 'default' : 'secondary'}
                  >
                    <Shield className="h-3 w-3 mr-1" />
                    {role}
                  </Badge>
                ))
              ) : (
                <Badge variant="outline">{user.primary_role || 'Aucun rôle'}</Badge>
              )}
            </div>
          </DetailSection>

          <DetailSection title="Actions" variant="card">
            <div className="space-y-2">
              {/* Activate/Deactivate */}
              {user.is_active ? (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleAction('deactivate', () => deactivateUser(user.user_id))}
                  disabled={actionLoading === 'deactivate'}
                >
                  {actionLoading === 'deactivate' ? (
                    <div className="h-5 w-5 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : <UserX className="h-5 w-5 mr-2" />}
                  Désactiver le compte
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleAction('reactivate', () => reactivateUser(user.user_id))}
                  disabled={actionLoading === 'reactivate'}
                >
                  {actionLoading === 'reactivate' ? (
                    <div className="h-5 w-5 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : <UserCheck className="h-5 w-5 mr-2" />}
                  Réactiver le compte
                </Button>
              )}

              {/* Lock/Unlock */}
              {user.is_locked ? (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleAction('unlock', () => unlockUser(user.user_id))}
                  disabled={actionLoading === 'unlock'}
                >
                  {actionLoading === 'unlock' ? (
                    <div className="h-5 w-5 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : <Unlock className="h-5 w-5 mr-2" />}
                  Déverrouiller le compte
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleAction('lock', () => lockUser(user.user_id, 24))}
                  disabled={actionLoading === 'lock'}
                >
                  {actionLoading === 'lock' ? (
                    <div className="h-5 w-5 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : <Lock className="h-5 w-5 mr-2" />}
                  Verrouiller le compte (24 h)
                </Button>
              )}

              {/* Anonymize (GDPR) */}
              <Button
                variant="outline"
                className="w-full justify-start text-orange-600"
                onClick={() => setShowAnonymizeConfirm(true)}
                disabled={actionLoading === 'anonymize'}
              >
                <UserX className="h-5 w-5 mr-2" />
                Anonymiser (RGPD)
              </Button>

              {/* Delete */}
              <Button
                variant="destructive"
                className="w-full justify-start"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={actionLoading === 'delete'}
              >
                <Trash2 className="h-5 w-5 mr-2" />
                Supprimer le compte
              </Button>

              <hr className="my-2" />

              {/* Impersonate */}
              <Button
                variant="default"
                className="w-full justify-start"
                onClick={() => setShowImpersonateModal(true)}
              >
                <LogIn className="h-5 w-5 mr-2" />
                Usurper l'identité
              </Button>

              {/* View related */}
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/merchants?userId=${user.user_id}`)}
              >
                <ShoppingBag className="h-5 w-5 mr-2" />
                Voir les commerçants
              </Button>
            </div>
          </DetailSection>
        </div>
      </div>

      {/* Impersonate Modal */}
      {showImpersonateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-2">Usurper l'identité de l'utilisateur</h3>
              <p className="text-sm text-gray-600 mb-4">
                Vous êtes sur le point d'usurper l'identité de <strong>{user.first_name} {user.last_name}</strong>
              </p>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Motif *</label>
                <textarea
                  value={impersonateReason}
                  onChange={(e) => setImpersonateReason(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  placeholder="Saisissez le motif..."
                />
              </div>
              <div className="mt-4 flex justify-end space-x-2">
                <Button variant="ghost" onClick={() => { setShowImpersonateModal(false); setImpersonateReason('') }}>Annuler</Button>
                <Button onClick={handleImpersonate} disabled={!impersonateReason.trim()}>
                  <LogIn className="h-4 w-4 mr-2" /> Usurper l'identité
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2 text-red-600 mb-4">
                <AlertTriangle className="h-6 w-6" />
                <h3 className="text-lg font-semibold">Supprimer le compte</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Êtes-vous sûr de vouloir supprimer ce compte ? Cette action peut être annulée en réactivant le compte.
              </p>
              <div className="flex justify-end space-x-2">
                <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>Annuler</Button>
                <Button variant="destructive" onClick={async () => {
                  await handleAction('delete', () => deactivateUser(user.user_id))
                  setShowDeleteConfirm(false)
                }}>
                  <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Anonymize Confirmation Modal */}
      {showAnonymizeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2 text-orange-600 mb-4">
                <AlertTriangle className="h-6 w-6" />
                <h3 className="text-lg font-semibold">Anonymiser l'utilisateur (RGPD)</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Cela anonymisera définitivement toutes les données personnelles de cet utilisateur. Cette action est irréversible.
              </p>
              <div className="flex justify-end space-x-2">
                <Button variant="ghost" onClick={() => setShowAnonymizeConfirm(false)}>Annuler</Button>
                <Button variant="destructive" onClick={async () => {
                  await handleAction('anonymize', () => anonymizeUser(user.user_id))
                  setShowAnonymizeConfirm(false)
                }}>
                  <UserX className="h-4 w-4 mr-2" /> Anonymiser
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export default UserDetails
